import { createFileRoute } from "@tanstack/react-router";
import { type CSSProperties, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AccountStatus, AssetNetwork } from "@cpg/common";
import { Download } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn, type DataTableGroupHeaderCell } from "@/components/data-table";
import { StatCard } from "@/components/stat-card";
import { Amount, formatUsdAmount } from "@/components/amount";
import { AddressWithVault } from "@/components/address-with-vault";
import { MerchantCell } from "@/components/merchant-cell";
import { AssetLabel } from "@/components/asset-label";
import { STATUS_DOT_COLOR, type StatusColor } from "@/lib/status-colors";
import { useTableState, type TableSearch } from "@/lib/table-state";
import { cn, formatStatusLabel } from "@/lib/utils";
import { ApiError } from "@/api/client";
import { listLedgers } from "@/api/ledgers";
import { exportAccountsCsv, getAvailableDates, getSnapshot, getTotals } from "@/api/accountant";
import { listMerchants } from "@/api/merchants";
import {
  type AccountantSnapshotItem,
  type LedgerListItem,
  type ListLedgersParams,
} from "@/api/types";
import { LedgerTransactionsDialog, type LedgerDrillDownTarget } from "./-ledger-transactions-dialog";

// Feature-028 Task 4: renamed from `_app.ledgers.tsx` (`/ledgers` -> `/accounts`)
// and reworked to add a date picker + historical (snapshot-backed) view on
// top of the Task-28 live table this route already had. Today keeps calling
// `GET /v1/ledgers` exactly as before (table unchanged); a past date instead
// calls `GET /v1/accountant/snapshot` (+ `GET /v1/accountant/totals` in both
// cases for the three widgets above the table) — see the plan's
// "apps/backoffice" technical-details section.
//
// `AccountsRow` is the one shape the table's columns render through
// regardless of source, so the live/`LedgerListItem` and
// past/`AccountantSnapshotItem` branches don't need two parallel column
// arrays. Historical rows carry no `address`/`fireblocksVaultId` (a snapshot
// never freezes the account's address) and no ledger ids (`merchantLedgerId`/
// `projectLedgerId` are `null`) — transaction drill-down is disabled for
// them entirely (transactions are always current data, never part of a
// frozen snapshot), enforced by only wiring `DataTable`'s `onRowClick` when
// `isToday`, never per-row.

type AccountsFilters = {
  merchantId?: string;
  network?: AssetNetwork;
  status?: AccountStatus;
  /** URL-persisted selected date (`YYYY-MM-DD`) — `undefined` means "today". */
  date?: string;
};

const DEFAULT_FILTERS: AccountsFilters = {};

/** Plain type-guarded passthrough — mirrors `_app.refunds.tsx`'s `validateSearch` house style. */
function validateAccountsSearch(search: Record<string, unknown>): TableSearch<AccountsFilters> {
  return {
    page: typeof search.page === "number" ? search.page : undefined,
    pageSize: typeof search.pageSize === "number" ? search.pageSize : undefined,
    sortBy: typeof search.sortBy === "string" ? search.sortBy : undefined,
    sortOrder: search.sortOrder === "asc" || search.sortOrder === "desc" ? search.sortOrder : undefined,
    merchantId: typeof search.merchantId === "string" ? search.merchantId : undefined,
    network: typeof search.network === "string" ? (search.network as AssetNetwork) : undefined,
    status: typeof search.status === "string" ? (search.status as AccountStatus) : undefined,
    date: typeof search.date === "string" ? search.date : undefined,
  };
}

const SELECT_CLASSNAME = cn(
  "h-9 rounded-md border border-input bg-transparent px-2 text-sm",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
);

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

// The three column groups (Merchant Ledger blue, Project Ledger purple,
// Balance green) tint from the shared StatusColor palette (`status-colors.ts`)
// instead of duplicated magic hex literals — the merchant detail page's own
// Accounts tab uses the same three colors for the same grouping. Two alpha
// levels per color (stronger group-header/column-header, lighter body) match
// that page's tinted-column depth convention. Inline `style`, not a Tailwind
// class: the scanner can only ever pick up literal `bg-[#xxxxxx]` strings
// actually present in source, not a class name built at runtime from a
// shared constant — see DataTableColumn's `headerStyle`/`cellStyle` doc
// comment.
function columnTint(color: StatusColor): { headerStyle: CSSProperties; cellStyle: CSSProperties } {
  const hex = STATUS_DOT_COLOR[color];
  return {
    headerStyle: { backgroundColor: `${hex}1A` },
    cellStyle: { backgroundColor: `${hex}0D` },
  };
}

function groupTint(color: StatusColor): { style: CSSProperties } {
  const hex = STATUS_DOT_COLOR[color];
  return { style: { backgroundColor: `${hex}1A`, color: hex } };
}

export const Route = createFileRoute("/_app/accounts")({
  validateSearch: validateAccountsSearch,
  component: AccountsPage,
});

// ─── accounts page ──────────────────────────────────────────────────────────

/** The one row shape the table renders through — see the file-header doc comment. */
interface AccountsRow {
  accountId: string;
  merchantId: string;
  merchantBusinessName: string;
  merchantPublicId: string;
  assetSymbol: string;
  assetNetwork: AssetNetwork;
  address: string | null;
  fireblocksVaultId: string | null;
  balanceTotal: string;
  balanceTotalUsd: string;
  balancePending: string;
  balancePendingUsd: string;
  balanceFrozen: string;
  balanceFrozenUsd: string;
  gasCostNative: string;
  gasCostUsd: string;
  projectLedgerBalanceNative: string;
  projectLedgerBalanceUsd: string;
  totalBalanceNative: string;
  totalBalanceUsd: string;
  merchantLedgerId: string | null;
  projectLedgerId: string | null;
  historical: boolean;
}

function fromLedgerItem(row: LedgerListItem): AccountsRow {
  return {
    accountId: row.accountId,
    merchantId: row.merchantId,
    merchantBusinessName: row.merchantBusinessName,
    merchantPublicId: row.merchantPublicId,
    assetSymbol: row.assetSymbol,
    assetNetwork: row.assetNetwork,
    address: row.address,
    fireblocksVaultId: row.fireblocksVaultId,
    balanceTotal: row.balanceTotal,
    balanceTotalUsd: row.balanceTotalUsd,
    balancePending: row.balancePending,
    balancePendingUsd: row.balancePendingUsd,
    balanceFrozen: row.balanceFrozen,
    balanceFrozenUsd: row.balanceFrozenUsd,
    gasCostNative: row.projectLedgerGasCostNative,
    gasCostUsd: row.projectLedgerGasCostUsd,
    projectLedgerBalanceNative: row.projectLedgerBalanceNative,
    projectLedgerBalanceUsd: row.projectLedgerBalanceUsd,
    totalBalanceNative: row.totalBalanceNative,
    totalBalanceUsd: row.totalBalanceUsd,
    merchantLedgerId: row.merchantLedgerId,
    projectLedgerId: row.projectLedgerId,
    historical: false,
  };
}

// Matches the live contract exactly (`ledgers.service.ts`'s `toLedgerItem`):
// "Available" (`balanceTotal`/`Usd`) is the merchant ledger's own balance
// (`merchantLedger.balanceNative` live, `merchantLedgerBalanceNative` here);
// "Total Balance" (`totalBalanceNative`/`Usd`) is the account's own stored
// `balanceTotal` (`account.balanceTotal` live, `balanceTotalNative` here) —
// nothing is added to it on either branch.
function fromSnapshotItem(row: AccountantSnapshotItem): AccountsRow {
  return {
    accountId: row.accountId,
    merchantId: row.merchantId,
    merchantBusinessName: row.merchantBusinessName,
    merchantPublicId: row.merchantPublicId,
    assetSymbol: row.assetSymbol,
    assetNetwork: row.assetNetwork,
    address: null,
    fireblocksVaultId: null,
    balanceTotal: row.merchantLedgerBalanceNative,
    balanceTotalUsd: row.merchantLedgerBalanceUsd,
    balancePending: row.balancePendingNative,
    balancePendingUsd: row.balancePendingUsd,
    balanceFrozen: row.balanceFrozenNative,
    balanceFrozenUsd: row.balanceFrozenUsd,
    gasCostNative: row.projectLedgerGasCostNative,
    gasCostUsd: row.projectLedgerGasCostUsd,
    projectLedgerBalanceNative: row.projectLedgerBalanceNative,
    projectLedgerBalanceUsd: row.projectLedgerBalanceUsd,
    totalBalanceNative: row.balanceTotalNative,
    totalBalanceUsd: row.balanceTotalUsd,
    merchantLedgerId: null,
    projectLedgerId: null,
    historical: true,
  };
}

function AccountsPage() {
  const tableState = useTableState<AccountsFilters>({ defaultFilters: DEFAULT_FILTERS });
  const [drillDown, setDrillDown] = useState<LedgerDrillDownTarget | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const today = todayIso();
  const selectedDate = tableState.filters.date ?? today;
  const isToday = selectedDate === today;

  const { data: availableDates } = useQuery({
    queryKey: ["accountant-available-dates"],
    queryFn: getAvailableDates,
  });

  const { data: totals } = useQuery({
    queryKey: ["accountant-totals", selectedDate],
    queryFn: () => getTotals(isToday ? undefined : selectedDate),
  });

  const liveParams: ListLedgersParams = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: (tableState.sortBy ?? undefined) as ListLedgersParams["sortBy"],
    sortOrder: tableState.sortOrder,
    merchantId: tableState.filters.merchantId,
    network: tableState.filters.network,
    status: tableState.filters.status,
  };

  const liveQuery = useQuery({
    queryKey: ["ledgers", liveParams],
    queryFn: () => listLedgers(liveParams),
    enabled: isToday,
  });

  const snapshotQuery = useQuery({
    queryKey: ["accountant-snapshot", selectedDate, tableState.page, tableState.pageSize],
    queryFn: () => getSnapshot({ date: selectedDate, page: tableState.page, pageSize: tableState.pageSize }),
    enabled: !isToday,
  });

  const rows: AccountsRow[] = isToday
    ? (liveQuery.data?.data ?? []).map(fromLedgerItem)
    : (snapshotQuery.data?.data ?? []).map(fromSnapshotItem);
  const total = isToday ? (liveQuery.data?.total ?? 0) : (snapshotQuery.data?.total ?? 0);
  const isPending = isToday ? liveQuery.isPending : snapshotQuery.isPending;
  const isError = isToday ? liveQuery.isError : snapshotQuery.isError;

  // Merchant filter is a name dropdown (not a raw UUID input) — reuses
  // listMerchants, same as _app.invoices.tsx/_app.refunds.tsx. Only rendered
  // in the filter bar for today (the snapshot endpoint has no filter params),
  // but harmless to keep fetched unconditionally — cheap and cached.
  const { data: merchants } = useQuery({
    queryKey: ["merchants"],
    queryFn: listMerchants,
  });
  const sortedMerchants = [...(merchants ?? [])].sort((a, b) =>
    a.businessName.localeCompare(b.businessName),
  );

  const anyFilterActive =
    tableState.filters.merchantId !== undefined ||
    tableState.filters.network !== undefined ||
    tableState.filters.status !== undefined;

  function openDrillDown(row: AccountsRow) {
    if (row.historical || row.merchantLedgerId === null || row.projectLedgerId === null) return;
    setDrillDown({
      type: "merchant",
      ledgerId: row.merchantLedgerId,
      otherLedgerId: row.projectLedgerId,
      assetSymbol: row.assetSymbol,
      subtitle: `${row.merchantBusinessName} · ${row.assetSymbol} · ${formatStatusLabel(row.assetNetwork)}`,
    });
  }

  // `exportAccountsCsv` fetches the CSV as a Bearer-authenticated blob (a
  // plain `<a href>` to the API would 401 — see `api/accountant.ts`'s
  // doc comment) and triggers the browser save itself, so this handler only
  // needs a loading guard + the same toast-on-error convention every other
  // mutating action in this app uses (e.g. `_app.assets.tsx`'s mutations).
  async function handleExport() {
    setIsExporting(true);
    try {
      await exportAccountsCsv(selectedDate);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to export CSV.");
    } finally {
      setIsExporting(false);
    }
  }

  const columns: DataTableColumn<AccountsRow>[] = [
    {
      key: "asset",
      header: "Asset",
      cell: (row) => <AssetLabel symbol={row.assetSymbol} network={row.assetNetwork} />,
    },
    {
      key: "merchant",
      header: "Merchant",
      // Not part of the row click (which opens the ledger drill-down
      // dialog) — this cell has its own navigation to the merchant page.
      cell: (row) => (
        <MerchantCell
          merchantId={row.merchantId}
          businessName={row.merchantBusinessName}
          publicId={row.merchantPublicId}
        />
      ),
    },
    {
      key: "address",
      header: "Address",
      // A snapshot row never freezes the account's address — historical rows
      // show a plain dash instead of AddressWithVault's explorer link/copy
      // button, which need a live address.
      cell: (row) =>
        row.address === null ? (
          <span className="text-sm text-muted-foreground">—</span>
        ) : (
          <span onClick={(e) => e.stopPropagation()}>
            <AddressWithVault address={row.address} vaultId={row.fireblocksVaultId} network={row.assetNetwork} />
          </span>
        ),
    },
    {
      key: "balanceTotal",
      header: "Available",
      ...columnTint("info"),
      cell: (row) => (
        <span className="block text-sm text-foreground">
          <Amount native={row.balanceTotal} symbol={row.assetSymbol} usd={row.balanceTotalUsd} />
        </span>
      ),
    },
    {
      key: "balancePending",
      header: "Processing",
      ...columnTint("info"),
      cell: (row) => (
        <span className="block text-sm text-foreground">
          <Amount native={row.balancePending} symbol={row.assetSymbol} usd={row.balancePendingUsd} />
        </span>
      ),
    },
    {
      key: "balanceFrozen",
      header: "Locked",
      ...columnTint("info"),
      cell: (row) => (
        <span className="block text-sm text-foreground">
          <Amount native={row.balanceFrozen} symbol={row.assetSymbol} usd={row.balanceFrozenUsd} />
        </span>
      ),
    },
    {
      key: "gasCostNative",
      header: "Gas spend",
      ...columnTint("purple"),
      cell: (row) => (
        <span className="block text-sm text-foreground">
          <Amount native={row.gasCostNative} symbol={row.assetSymbol} usd={row.gasCostUsd} />
        </span>
      ),
    },
    {
      key: "projectLedgerBalanceNative",
      header: "Project fees",
      ...columnTint("purple"),
      cell: (row) => (
        <span className="block text-sm text-foreground">
          <Amount
            native={row.projectLedgerBalanceNative}
            symbol={row.assetSymbol}
            usd={row.projectLedgerBalanceUsd}
          />
        </span>
      ),
    },
    {
      key: "totalBalanceNative",
      header: "Total Balance",
      ...columnTint("success"),
      cell: (row) => (
        <span className="block text-sm text-foreground">
          <Amount native={row.totalBalanceNative} symbol={row.assetSymbol} usd={row.totalBalanceUsd} />
        </span>
      ),
    },
  ];

  // Sums to `columns.length` (9) — Asset/Merchant/Address ungrouped, then
  // the three color-coded spans.
  const groupHeaders: DataTableGroupHeaderCell[] = [
    { label: "", colSpan: 3 },
    { label: "Merchant Ledger", colSpan: 3, ...groupTint("info") },
    { label: "Project Ledger", colSpan: 2, ...groupTint("purple") },
    { label: "Balance", colSpan: 1, ...groupTint("success") },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          {isToday && (
            <>
              <select
                aria-label="Merchant"
                className={SELECT_CLASSNAME}
                value={tableState.filters.merchantId ?? "ALL"}
                onChange={(e) =>
                  tableState.setFilter("merchantId", e.target.value === "ALL" ? undefined : e.target.value)
                }
              >
                <option value="ALL">All merchants</option>
                {sortedMerchants.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.businessName}
                  </option>
                ))}
              </select>
              <select
                aria-label="Network"
                className={SELECT_CLASSNAME}
                value={tableState.filters.network ?? "ALL"}
                onChange={(e) =>
                  tableState.setFilter(
                    "network",
                    e.target.value === "ALL" ? undefined : (e.target.value as AssetNetwork),
                  )
                }
              >
                <option value="ALL">All networks</option>
                {Object.values(AssetNetwork).map((n) => (
                  <option key={n} value={n}>
                    {formatStatusLabel(n)}
                  </option>
                ))}
              </select>
              {anyFilterActive && (
                <Button type="button" variant="ghost" size="sm" onClick={() => tableState.reset()}>
                  Clear filters
                </Button>
              )}
            </>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <input
            aria-label="Date"
            type="date"
            className={SELECT_CLASSNAME}
            value={selectedDate}
            min={availableDates?.minDate ?? today}
            max={today}
            onChange={(e) => {
              const value = e.target.value;
              if (value === today) {
                tableState.setFilter("date", undefined);
                return;
              }
              // A bare `<input type="date">` can't natively disable
              // individual gap dates (a day the job failed to run) inside
              // `[minDate, today]` — only the min/max bounds. Reject a picked
              // gap date on change instead: the input is controlled by
              // `selectedDate`, so not calling `setFilter` here simply
              // reverts it. Skipped entirely while `availableDates` hasn't
              // loaded yet, so the picker isn't spuriously locked to
              // "today" during the initial fetch.
              if (availableDates && !availableDates.dates.includes(value)) {
                toast.error("No snapshot data for that date — pick a date the Accountant job ran on.");
                return;
              }
              tableState.setFilter("date", value);
            }}
          />
          <Button type="button" variant="outline" size="sm" onClick={handleExport} disabled={isExporting}>
            <Download />
            {isExporting ? "Exporting…" : "Export CSV"}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          label="Total Merchant Ledger"
          value={formatUsdAmount(totals?.merchantLedgerTotalUsd ?? null)}
          tone="info"
        />
        <StatCard
          label="Total Project Ledger"
          value={formatUsdAmount(totals?.projectLedgerTotalUsd ?? null)}
          tone="purple"
        />
        <StatCard
          label="Total Wallet Balance"
          value={formatUsdAmount(totals?.walletBalanceTotalUsd ?? null)}
          tone="success"
        />
      </div>

      <DataTable
        columns={columns}
        groupHeaders={groupHeaders}
        onRowClick={isToday ? openDrillDown : undefined}
        items={rows}
        total={total}
        page={tableState.page}
        pageSize={tableState.pageSize}
        sortBy={tableState.sortBy}
        sortOrder={tableState.sortOrder}
        loading={isPending}
        error={isError ? "Failed to load accounts." : null}
        onPageChange={(page) => tableState.setPage(page)}
        onPageSizeChange={(pageSize) => tableState.setPageSize(pageSize)}
        onSortChange={(columnKey) => tableState.setSort(columnKey)}
        getRowKey={(row) => row.accountId}
        emptyMessage={isToday ? "No funded accounts yet." : "No snapshot data for this date."}
      />

      <LedgerTransactionsDialog
        target={drillDown}
        onOpenChange={(open) => {
          if (!open) setDrillDown(null);
        }}
      />
    </div>
  );
}
